/**
 * @module core/auth/token-manager
 * @description Manages Firebase authentication tokens including refresh logic,
 * automatic retry on 401 errors, and authenticated fetch requests
 */

import { signOutUser } from './auth-manager.js';
import { logger } from '../../utils/logger.js';
import { showError } from '../../ui/components/error-display.js';

/**
 * Token refresh interval ID for cleanup
 * @type {number|null}
 */
let tokenRefreshInterval = null;

/**
 * Reference to Firebase auth instance (set by auth-manager)
 * @type {Object|null}
 */
let authInstance = null;

/**
 * Initialize token manager with Firebase auth instance
 * Called by auth-manager during initialization
 *
 * @param {Object} auth - Firebase auth instance
 */
export function initializeTokenManager(auth) {
    authInstance = auth;
}

/**
 * Get a valid Firebase ID token, with optional force refresh
 *
 * @param {boolean} forceRefresh - Force token refresh even if not expired
 * @returns {Promise<string>} Valid Firebase ID token
 * @throws {Error} If no authenticated user
 *
 * @example
 * const token = await getValidToken();           // Get current token
 * const newToken = await getValidToken(true);    // Force refresh
 */
export async function getValidToken(forceRefresh = false) {
    if (!authInstance) {
        throw new Error('Token manager not initialized. Call initializeTokenManager first.');
    }

    const user = authInstance.currentUser;
    if (!user) {
        throw new Error('No authenticated user');
    }

    // getIdToken() with forceRefresh=true will get a new token
    return await user.getIdToken(forceRefresh);
}

/**
 * Fetch with automatic token refresh on expiration
 * Automatically handles 401 errors by refreshing token and retrying
 *
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 * @throws {Error} If authentication fails after token refresh
 *
 * @example
 * const response = await fetchWithAuth('/api/files', {
 *   method: 'GET'
 * });
 *
 * const data = await response.json();
 */
export async function fetchWithAuth(url, options = {}) {
    const makeRequest = async (token) => fetch(url, {
        ...options,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...options.headers
        }
    });

    // First attempt with current token
    let token = await getValidToken();
    let response = await makeRequest(token);

    // If we get 401, try refreshing the token
    if (response.status === 401) {
        const errorData = await response.json().catch(() => ({}));

        // Check if it's specifically a token expiration
        if (errorData.error === 'Token Expired' ||
            errorData.error === 'Invalid or expired token' ||
            errorData.error === 'Authentication Error') {
            logger.info('Token expired, refreshing and retrying...');

            // Force token refresh
            token = await getValidToken(true);

            // Retry the request with new token
            response = await makeRequest(token);

            // If still failing, user needs to re-authenticate
            if (response.status === 401) {
                logger.error('Authentication failed after token refresh');

                // Track authentication failure
                if (monitoring.isInitialized()) {
                    monitoring.trackError(new Error('Authentication failed after token refresh'), {
                        category: ErrorCategory.AUTHENTICATION,
                        severity: ErrorSeverity.ERROR,
                        operation: 'fetchWithAuth_retry',
                        statusCode: 401
                    });
                }

                // Sign out user since authentication failed after token refresh
                await signOutUser();
                showError('Your session has expired. Please sign in again.');
                throw new Error('Authentication failed after token refresh');
            }
        }
    }

    return response;
}

/**
 * Start proactive token refresh interval
 * Refreshes token every 50 minutes (tokens expire after 60 minutes)
 *
 * @param {Object} user - Firebase user object
 *
 * @example
 * startTokenRefreshInterval(user);
 */
export function startTokenRefreshInterval(user) {
    // Clear any existing interval first
    stopTokenRefreshInterval();

    if (!user) {
        logger.warn('Cannot start token refresh interval: no user provided');
        return;
    }

    // Set up proactive token refresh (every 50 minutes, tokens expire after 60)
    tokenRefreshInterval = setInterval(async () => {
        try {
            await user.getIdToken(true);
            logger.debug('Token proactively refreshed');
        } catch (error) {
            logger.error('Failed to refresh token:', error);

            // Track token refresh failure
            if (monitoring.isInitialized()) {
                monitoring.trackError(error, {
                    category: ErrorCategory.AUTHENTICATION,
                    severity: ErrorSeverity.WARNING,
                    operation: 'proactive_token_refresh'
                });
            }
        }
    }, 50 * 60 * 1000); // 50 minutes

    logger.debug('Token refresh interval started (every 50 minutes)');
}

/**
 * Stop the token refresh interval
 * Should be called when user signs out
 *
 * @example
 * stopTokenRefreshInterval();
 */
export function stopTokenRefreshInterval() {
    if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
        tokenRefreshInterval = null;
        logger.info('Token refresh interval stopped');
    }
}

/**
 * Check if token refresh interval is active
 *
 * @returns {boolean} True if interval is running
 */
export function isTokenRefreshActive() {
    return tokenRefreshInterval !== null;
}
